'use client';

import { useSearchParams, usePathname } from 'next/navigation';
import { useCallback, useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import dynamic from 'next/dynamic';
import CarouselDisplayTrigger from '@alltrails/amplitude/enums/CarouselDisplayTrigger';
import UpsellTrigger from '@alltrails/amplitude/enums/UpsellTrigger';
import logTopModuleSwiped from '@alltrails/amplitude/events/logTopModuleSwiped';
import logTrailDetailsHeroImageClicked from '@alltrails/amplitude/events/logTrailDetailsHeroImageClicked';
import getPhotoUrl from '@alltrails/core/utils/getPhotoUrl';
import type TrailDetails from '@alltrails/core/types/TrailDetails';
import type TrailPhoto from '@alltrails/types/TrailPhoto';
import type { TrailId } from '@alltrails/types/TrailId';
import SwiperCarousel from '@alltrails/denali/components/SwiperCarousel';
import useUser from '@alltrails/context/hooks/useUser';
import { useLanguageRegionCode } from '@alltrails/core/hooks/useLanguageRegionCode';
import useInstallPrompt from '@alltrails/install-prompt/hooks/useInstallPrompt';
import useRoadblock from '@alltrails/layout/hooks/useRoadblock';
import { TrailPage, useTrailPhotos, type TrailPhotoLocationInfo } from '@alltrails/trails';
import useIsMobileSizedScreen from '@alltrails/denali/hooks/useIsMobileSizedScreen';
import logTrailDetailsTourButtonClicked from '@alltrails/amplitude/events/logTrailDetailsTourButtonClicked';
import logTrailDetailsAddPhotoClicked from '@alltrails/amplitude/events/logTrailDetailsAddPhotoClicked';
import { ModalPortal } from '@alltrails/core';
import { handleClosePhotoTour, handleOpenPhotoTour } from '@alltrails/photo-tour';
import { useDispatch } from 'react-redux';
import Toast from '@alltrails/denali/components/Toast';
import StaticMap from '../StaticMap';
import HeroImage from '../HeroImage/HeroImage';
import PhotoTourButton from '../DesktopHeroGrid/PhotoTourButton';
import AddPhotoButton from '../DesktopHeroGrid/AddPhotoButton';
import MobileHeroImage from './MobileHeroImage';
import styles from './MobileHero.module.scss';
import useTrailDefaultImage from '@/hooks/useTrailDefaultImage';
import useIsTrailMapOpen from '@/hooks/useIsTrailMapOpen';

const SignUpModal = dynamic(() => import('@alltrails/user-auth/components/SignUpModal'), { ssr: false });
const TrailMap = dynamic(() => import('../TrailMap'), { ssr: false });
const InstallInterstitial = dynamic(() => import('@alltrails/install-prompt/components/InstallInterstitial'), { ssr: false });
const PhotoTourModal = dynamic(() => import('@alltrails/photo-tour').then(module => ({ default: module.PhotoTourModal })), { ssr: false });
const PhotoUploadModal = dynamic(() => import('@/components/PhotoUploadModal/PhotoUploadModalWrapper'), { ssr: false });

const photoCarouselCount = 6;

type MobileHeroProps = {
  defaultPhoto?: TrailPhoto;
  mapId: number;
  photoCount?: number;
  photoPreviewId?: string;
  trailInfo: TrailPhotoLocationInfo & { id: TrailId };
  length?: number;
  hasPhotoTour: boolean;
  isScrollLimitActive?: boolean;
  isBot?: boolean;
  trailDetails: TrailDetails;
  trailPage: TrailPage;
};

const MobileHero = ({
  defaultPhoto,
  mapId,
  photoCount,
  photoPreviewId,
  trailInfo,
  length,
  hasPhotoTour,
  isScrollLimitActive,
  isBot = false,
  trailDetails,
  trailPage
}: MobileHeroProps) => {
  const intl = useIntl();
  const isMobile = useIsMobileSizedScreen();
  const dispatch = useDispatch();
  const languageRegionCode = useLanguageRegionCode();
  const user = useUser();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const swiped = useRef(false);
  const [isSignupModalOpen, setIsSignupModalOpen] = useState(false);
  const [isInterstitialOpen, setIsInterstitialOpen] = useInstallPrompt('interstitial', false);
  const [isPhotoTourOpen, setIsPhotoTourOpen] = useState(false);
  const [wasPhotoTourClicked, setWasPhotoTourClicked] = useState(false);
  const [wasAddPhotosClicked, setWasAddPhotosClicked] = useState(false);
  const { isTrailMapOpen, openTrailMap } = useIsTrailMapOpen();
  const [isPhotoUploadModalOpen, setIsPhotoUploadModalOpen] = useState(false);
  const [signUpModalTrigger, setSignUpModalTrigger] = useState<CarouselDisplayTrigger>('View Trail Photos');
  const [upsellTrigger, setUpsellTrigger] = useState<UpsellTrigger>('View Trail Photos');
  const [isPhotoToastOpen, setIsPhotoToastOpen] = useState(false);
  const { photos, loadPhotos, loadState } = useTrailPhotos({ defaultPhoto, perPage: photoCarouselCount, trailId: trailInfo.id });
  const { defaultPhotoAlt, defaultPhotoUrl } = useTrailDefaultImage({
    defaultPhoto,
    mapId,
    photoPreviewId,
    photoSize: '1080x700',
    trailPhotoLocationInfo: trailInfo,
    trailDetails
  });

  const handleOpenTour = useCallback(() => {
    dispatch(
      handleOpenPhotoTour({
        tourType: 'trail',
        tourArgs: { trailId: trailInfo.id, isMobile }
      })
    );
    setIsPhotoTourOpen(true);
  }, [dispatch, isMobile, trailInfo.id]);

  const onRoadblock = useRoadblock({
    afterRoadblock: openTrailMap,
    eventName: 'trail_explore',
    languageRegionCode,
    returnToUrl: `${pathname}?${searchParams}`,
    user,
    setIsInterstitialOpen
  });

  const onPhotoClick = useCallback(() => {
    logTrailDetailsHeroImageClicked({ trail_id: trailInfo.id });
    if (!user) {
      setSignUpModalTrigger('View Trail Photos');
      setUpsellTrigger('View Trail Photos');
      setIsSignupModalOpen(true);
    } else {
      window.location.assign(`/trail/${trailInfo.slug}/photos`);
    }
  }, [trailInfo.id, trailInfo.slug, user]);

  const onMapFallbackClick = useCallback(() => {
    logTrailDetailsHeroImageClicked({ trail_id: trailInfo.id });
    if (!user) {
      setSignUpModalTrigger('View Static Map');
      setUpsellTrigger('View Static Map');
      setIsSignupModalOpen(true);
    } else {
      onRoadblock();
    }
  }, [onRoadblock, trailInfo.id, user]);

  useEffect(() => {
    loadPhotos();
  }, [loadPhotos, trailInfo.id]);

  return (
    <>
      <div className={styles.container}>
        {!!photoCount && !!defaultPhoto ? (
          <SwiperCarousel
            arrowStyle="subtle-with-standard-arrows"
            className={styles.carouselContainer}
            getItemKey={(photo: any) => String(photo.id)}
            items={photos}
            hasPaginationGradient
            hideSlideOverflow
            onSlideChange={() => {
              if (!swiped.current) {
                logTopModuleSwiped({ id: trailInfo.id.toString(), page_type: 'trail page' });
                swiped.current = true;
              }
            }}
            renderItem={(photo, photoIndex) => {
              const alt = photoIndex === 0 ? defaultPhotoAlt : trailInfo.name;
              const url =
                photoIndex === 0
                  ? defaultPhotoUrl
                  : getPhotoUrl({
                      isOptimized: true,
                      photo,
                      size: '1080x700'
                    });
              return (
                <MobileHeroImage
                  alt={alt}
                  onPhotoClick={onPhotoClick}
                  photoCount={photoCount}
                  photoIndex={photoIndex}
                  photoUrl={url}
                  showPhotosCta={loadState === 'loaded' && photos.length > 1 && photoIndex === photos.length - 1}
                  trailSlug={trailInfo.slug}
                />
              );
            }}
            scrollMode={photos.length < 2}
            showPaginationDots={true}
            uniqueId="trail-photos-mobile-carousel"
          />
        ) : (
          <button
            aria-label={intl.formatMessage({ defaultMessage: '{name} map' }, { name: trailInfo.name })}
            className={styles.staticMapHero}
            data-testid="static-map-hero"
            onClick={onMapFallbackClick}
          >
            <HeroImage alt={defaultPhotoAlt} isLcp photoUrl={defaultPhotoUrl} className={styles.heroImage} quality={75} />
          </button>
        )}
        {hasPhotoTour && (
          <PhotoTourButton
            isMobile
            onClick={() => {
              logTrailDetailsTourButtonClicked({ trail_id: trailInfo.id, web_location: 'gallery' });
              if (!user) {
                setSignUpModalTrigger('Gallery Tour');
                setUpsellTrigger('Gallery Tour');
                setWasPhotoTourClicked(true);
                setIsSignupModalOpen(true);
              } else {
                handleOpenTour();
              }
            }}
          />
        )}
        {!photoCount && (
          <AddPhotoButton
            handleClick={() => {
              logTrailDetailsAddPhotoClicked({
                trail_id: trailInfo.id
              });
              if (!user) {
                setSignUpModalTrigger('Add Photo to Trail');
                setUpsellTrigger('Add Photo to Trail');
                setWasAddPhotosClicked(true);
                setIsSignupModalOpen(true);
              } else {
                setIsPhotoUploadModalOpen(true);
              }
            }}
            isMobile
          />
        )}
        {!!defaultPhoto && !!photoCount && !isBot && (
          <div className={styles.staticMap}>
            <StaticMap mapId={mapId} trailName={trailInfo.name} isMobile isScrollLimitActive={isScrollLimitActive} trailId={trailDetails.id} />
          </div>
        )}
        {isTrailMapOpen && <TrailMap isMobile={isMobile} trailName={trailInfo.name} trailDetails={trailDetails} trailPage={trailPage} />}
        {isSignupModalOpen && (
          <SignUpModal
            isOpen={isSignupModalOpen}
            closeModal={() => {
              setIsSignupModalOpen(false);
            }}
            deeplinkData={{ pageId: trailInfo.id, pageType: 'trail' }}
            onSuccess={() => {
              setIsSignupModalOpen(false);
              if (wasPhotoTourClicked) {
                handleOpenTour();
              } else if (wasAddPhotosClicked) {
                setIsPhotoUploadModalOpen(true);
              } else if (!!photoCount && !!defaultPhoto) {
                window.location.assign(`/trail/${trailInfo.slug}/photos`);
              } else {
                openTrailMap();
              }
            }}
            trigger={signUpModalTrigger}
            upsellTrigger={upsellTrigger}
          />
        )}
        {isInterstitialOpen && (
          <InstallInterstitial
            preserveInterstitial={false}
            deeplinkData={{ pageId: trailInfo.id, pageType: 'trail' }}
            isOpen={isInterstitialOpen}
            onRequestClose={() => setIsInterstitialOpen(false)}
            promptLocation="trail page"
            isScrollLimitActive={isScrollLimitActive}
            variant="generic"
          />
        )}
        {(defaultPhoto || photos?.[0]) && isPhotoTourOpen && (
          <PhotoTourModal
            trailId={trailInfo.id}
            totalDistance={length}
            segmentActualDistances={length ? [length] : undefined}
            onClose={() => {
              setIsPhotoTourOpen(false);
              dispatch(handleClosePhotoTour());
            }}
            defaultPhoto={defaultPhoto || photos?.[0]}
            secondPhoto={photos?.[1]}
            name={trailInfo.name}
            hidePageScrollbarGutter
          />
        )}
      </div>
      {isPhotoUploadModalOpen && (
        <ModalPortal>
          <PhotoUploadModal
            closeModal={() => setIsPhotoUploadModalOpen(false)}
            trailId={trailInfo.id}
            onPhotosAdd={() => setIsPhotoToastOpen(true)}
            shouldHideSuccess
          />
        </ModalPortal>
      )}
      {isPhotoToastOpen && (
        <Toast
          isOpen={isPhotoToastOpen}
          message={<FormattedMessage defaultMessage="Success! Your photo will be available soon." />}
          type="neutral"
          testId="photo-upload-toast"
          timeout={3000}
          setIsOpen={setIsPhotoToastOpen}
          position="alternative"
        />
      )}
    </>
  );
};

export default MobileHero;
